import React from 'react';
import I18n from 'i18n-js';
import { RCDrawer, RCIcon, RCLoadingBars, RCPopover, useMediaQuery } from '@reverbdotcom/cadence/components';
import { BellIcon } from '@reverbdotcom/cadence/icons/react';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useNotificationsBellCount } from './notifications_bell_count_hook';
import { useNotificationsCenter } from './notifications_center_hook';
import ActionsSection from './actions_section';
import ActionsRollupView from './actions_rollup_view';
import TasksSection from './tasks_section';
import StreamSection from './stream_section';

export const COMPONENT_NAME = 'NotificationsCenterPopover';

export const MAX_BELL_COUNT = 99;

type PanelView = 'root' | 'actionsRollup';

export default function NotificationsCenterPopover() {
  const { count } = useNotificationsBellCount();
  const [isOpen, setIsOpen] = React.useState(false);
  const [view, setView] = React.useState<PanelView>('root');
  const {
    data,
    loading,
    loadMoreStream,
    hasMoreStream,
  } = useNotificationsCenter(isOpen);
  // Null until the browser reports, and on the server, so fall back to desktop.
  const isMobile = useMediaQuery('mobile') ?? false;

  function handleOpenChange(open: boolean) {
    if (open) {
      trackEvent({
        eventName: MParticleEventName.OpenedNotificationPanel,
        componentName: COMPONENT_NAME,
      });
    }

    setIsOpen(open);
    if (!open) { setView('root'); }
  }

  const actions = data?.notificationActions?.actions ?? [];
  const rollup = data?.notificationActions?.rollup ?? null;
  const tasks = data?.notificationTasks?.tasks ?? [];
  const streamItems = data?.notificationStream?.items ?? [];
  const totalUnread = data?.notificationStream?.totalUnread ?? 0;

  const showingLoader = loading && !data;

  const panelRef = React.useRef<HTMLDivElement>(null);

  const rollupTriggerRef = React.useRef<HTMLButtonElement>(null);
  const rollupBackRef = React.useRef<HTMLButtonElement>(null);
  // Which control the next view swap hands focus to. Both panes stay mounted and the one
  // going offscreen only takes `visibility: hidden` once its slide finishes, so the control
  // that was just pressed is still focused when it goes hidden - at which point the browser
  // drops focus to <body> and a screen reader resumes from the top of the page rather than
  // from the pane that just slid in. Moving focus on the commit gets it out first.
  const pendingFocusRef = React.useRef<React.RefObject<HTMLElement> | null>(null);

  // Keyed on the view so it runs after the swap commits, and only ever to a target one of
  // the two handlers below set - closing the panel resets the view too, and that must not
  // pull focus back out of the page.
  React.useEffect(() => {
    const target = pendingFocusRef.current;
    pendingFocusRef.current = null;
    target?.current?.focus();
  }, [view]);

  function openRollup() {
    pendingFocusRef.current = rollupBackRef;
    setView('actionsRollup');
  }

  function closeRollup() {
    pendingFocusRef.current = rollupTriggerRef;
    setView('root');
  }

  const showingRollup = view === 'actionsRollup';
  const bellCount = count > MAX_BELL_COUNT
    ? I18n.t('discovery.notificationsCenter.maxCount', { count: MAX_BELL_COUNT })
    : count || null;

  const bell = (
    <button
      type="button"
      className="reverb-header__nav__link reverb-header__nav__link--notification-icon"
    >
      <div
        className="reverb-header__nav__link__icon notifications-center__bell"
        data-notification-count={bellCount}
      >
        <RCIcon icon={<BellIcon />} />
      </div>
      <div className="reverb-header__nav__link__label">
        {I18n.t('discovery.reverbHeader.notifications')}
      </div>
      <span className="reverb-header__count-badge">
        {count > 0 && I18n.t('discovery.reverbHeader.notificationCount', { count })}
      </span>
    </button>
  );

  const panel = (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="notifications-center__viewport"
      data-testid="notifications-center-placeholder"
    >
      {showingLoader ? (
        <div className="d-flex fx-justify-center ptb-8">
          <RCLoadingBars />
        </div>
      ) : (
        <div
          className="notifications-center__slider"
          data-showing-rollup={showingRollup || undefined}
        >
          <div
            className="notifications-center__pane notifications-center__pane--root"
            aria-hidden={showingRollup}
          >
            <div className="notifications-center">
              {/* AR-669 took the visible title off desktop. The heading stays for screen
                  readers, which otherwise get a dialog with no landmark to jump to. Mobile
                  renders its own visible one in the drawer header. */}
              {!isMobile && (
                <h2 className="visually-hidden">
                  {I18n.t('discovery.notificationsCenter.title')}
                </h2>
              )}
              <ActionsSection
                rollup={rollup}
                rollupTriggerRef={rollupTriggerRef}
                onOpenRollup={openRollup}
              />
              <TasksSection tasks={tasks} />
              <StreamSection
                items={streamItems}
                totalUnread={totalUnread}
                hasMore={hasMoreStream}
                onLoadMore={loadMoreStream}
              />
            </div>
          </div>

          <div
            className="notifications-center__pane notifications-center__pane--rollup"
            aria-hidden={!showingRollup}
          >
            <ActionsRollupView
              actions={actions}
              backRef={rollupBackRef}
              onBack={closeRollup}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Mobile web gets a full-viewport drawer, matching the menu beside it. The
  // popover's hover-to-open has no meaning on touch, so the drawer opens on
  // press and closes from its own header button.
  if (isMobile) {
    return (
      <RCDrawer
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        title={I18n.t('discovery.notificationsCenter.title')}
        position="right"
        trigger={bell}
        initialFocus={panelRef}
      >
        {panel}
      </RCDrawer>
    );
  }

  return (
    <RCPopover
      onOpenChange={handleOpenChange}
      position="bottom-end"
      size="large"
      anchor={bell}
      openOnHover
      // The bell opens on hover, so pulling focus into the panel paints a focus ring on
      // whichever row happens to be first and scrolls the body to it, neither of which the
      // user asked for by moving the mouse. Focus stays on the bell; the panel is non-modal,
      // so tabbing on from it still walks into the content.
      initialFocus={-1}
    >
      {panel}
    </RCPopover>
  );
}
